import { FlagOutlined, PlusCircleOutlined, SaveOutlined, FileOutlined, SafetyOutlined, PullRequestOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Form, Input, message, Modal, Row, Select, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED, formatApiDate } from '@/config';
import BusBrandInput from '@/components/FormItems/BusBrandInput';
import BusModelInput from '@/components/FormItems/BusModelInput';
import SeatingModel from '@/components/app/Bus/SeatingModel';
import SeatingModelInput from '@/components/FormItems/SeatingModelInput';
import TransportVendorInput from '@/components/FormItems/TransportVendorInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import ColorPicker from '@/components/FormItems/ColorPicker';
import SeatingSummary from './SeatingSummary';
import BusOtherInfo from './BusOtherInfo';
import BusDocumentsModal from './BusDocumentsModal';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import BusInsuranceModal from './BusInsuranceModal';
import BusStationMappingsModal from './BusStationMappingsModal';
import { TransportRoleType } from '@/constants';
import { getFullName } from '@/helpers/string';
import { clearAllBussesCache } from '@/hooks/data/masterDataHook';
import { BusDto, CompanyTicketClassDto, UserBasicInfoModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'busesPage_Title';

const columns: IColumnType<BusDto>[] = [
  {
    title: 'busesPage_BusName',
    dataIndex: 'busName',
    ellipsis: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'busesPage_BusBrand',
    dataIndex: 'busBrandName',
    ellipsis: true,
  },
  {
    title: 'busesPage_BusModel',
    dataIndex: 'busModelName',
    ellipsis: true,
  },
  {
    title: 'busesPage_SerialNo',
    dataIndex: 'serialNo',
    ellipsis: true,
  },
  {
    title: 'busesPage_ChassisNo',
    dataIndex: 'chassisNo',
    ellipsis: true,
  },
  {
    title: 'busesPage_SeatingModel',
    dataIndex: 'seatingModelName',
    ellipsis: true,
  },
  {
    title: 'busesPage_PlateNumber',
    dataIndex: 'plateNumber',
    ellipsis: true,
  },
  {
    title: 'busesPage_OtherDetail',
    dataIndex: 'id',
    ellipsis: true,
    render: (id, bus) => {
      return <BusOtherInfo bus={bus} />;
    },
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'busName', label: 'busModelsPage_BusModelName' },
  { type: 'TEXT', name: 'plateNumber', label: 'busesPage_PlateNumber' },
  { type: 'TEXT', name: 'serialNo', label: 'busesPage_SerialNo' },
];

const BusesPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ticketClasses, setTicketClasses] = React.useState<CompanyTicketClassDto[]>([]);
  const [busDocumentId, setBusDocumentId] = React.useState<string>();
  const [busInsuranceId, setBusInsuranceId] = React.useState<string>();
  const [bus2StationId, setBus2StationId] = React.useState<string>();
  const [busDrivers, setBusDrivers] = React.useState<UserBasicInfoModel[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const canUpdate = useCanManageFleetTrip();

  const clearCache = () => {
    clearAllBussesCache();
  };

  React.useEffect(() => {
    apiSdk.BusService.getExportColumns().then(res => {
      setExportColumns(res);
    });
    apiSdk.CompanyUserService.getUserByRoles({ roles: [TransportRoleType.CoDriver, TransportRoleType.Driver] }).then(res => {
      setBusDrivers(res || []);
    });
  }, []);

  React.useEffect(() => {
    apiSdk.CompanyTicketClassService.getCompanyTicketClasses({ pageSize: 100 }).then(res => {
      setTicketClasses(res.data || []);
    });
  }, []);
  const createEntity = values => {
    setLoading(true);
    apiSdk.BusService.create({ body: { ...values, acquisitionDate: values.acquisitionDate ? formatApiDate(values.acquisitionDate) : undefined } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.BusService.update({ body: { ...values, acquisitionDate: values.acquisitionDate ? formatApiDate(values.acquisitionDate) : undefined } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onExportExcel = (cols: string[]) => {
    exportService
      .exportBuses({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toExcel();
  };

  const onExportPdf = (cols: string[]) => {
    exportService
      .exportBuses({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toPdf();
  };
  const seatingModelId = Form.useWatch('seatingModelId', form);

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setIsNewForm(true);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/buses/get-buses"
          bordered
          searchBtnAlignLeft
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={canUpdate}
          actionColumnWidth={180}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          extraBtns={
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
          }
          extraActions={
            canUpdate
              ? [
                  {
                    render: record => {
                      return (
                        <Tooltip title={t('busStationMappingsPage_Title')}>
                          <Button size="small" onClick={() => setBus2StationId(record.id)} type="default" icon={<PullRequestOutlined />} />
                        </Tooltip>
                      );
                    },
                  },
                  {
                    render: record => {
                      return (
                        <Tooltip title={t('busesPage_BusInsurances')}>
                          <Button size="small" onClick={() => setBusInsuranceId(record.id)} danger type="primary" icon={<SafetyOutlined />} />
                        </Tooltip>
                      );
                    },
                  },
                  {
                    render: record => {
                      return (
                        <Tooltip title={t('busesPage_Documents')}>
                          <Button size="small" onClick={() => setBusDocumentId(record.id)} type="primary" icon={<FileOutlined />} />
                        </Tooltip>
                      );
                    },
                  },
                ]
              : undefined
          }
          allowDelete={canUpdate}
          delete_url="/fleets/buses/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        />
        {formVisible ? (
          <Modal
            style={{ top: 10 }}
            visible={formVisible}
            maskClosable={false}
            width={ModalWidth.extraLarge}
            className="nta-custom-form"
            title={isNewForm ? t('busesPage_CreateBus') : t('busesPage_UpdateBus')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(() => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="vertical">
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Card className="mtopo-card" size="small" title={t('busesPage_Title')}>
                  <Row gutter={ROW_GUTTER}>
                    <Col md={6}>
                      <Form.Item label={t('busesPage_BusName')} name="busName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col md={6}>
                      <Form.Item label={t('busesPage_BusBrand')} name="busBrandId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <BusBrandInput />
                      </Form.Item>
                    </Col>
                    <Col md={6}>
                      <Form.Item label={t('busesPage_BusModel')} name="busModelId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <BusModelInput />
                      </Form.Item>
                    </Col>
                    <Col md={6}>
                      <Form.Item label={t('busesPage_PlateNumber')} name="plateNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
                <Row gutter={ROW_GUTTER}>
                  <Col md={6}>
                    <Card size="small" className="mtopo-card" title={t('busesPage_VendorDetail')}>
                      <Row gutter={ROW_GUTTER}>
                        <Col md={24}>
                          <Form.Item label={t('busesPage_transportVendor')} name="transportVendorId">
                            <TransportVendorInput />
                          </Form.Item>
                        </Col>
                        <Col md={24}>
                          <Form.Item label={t('busesPage_PurchaseOrderDetail')} name="purchaseOrderDetailId">
                            <Select />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                    <Card size="small" className="mtopo-card" title={t('busesPage_OtherDetail')}>
                      <Row gutter={ROW_GUTTER}>
                        <Col md={24}>
                          <Form.Item label={t('busesPage_SerialNo')} name="serialNo" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col md={24}>
                          <Form.Item label={t('busesPage_ChassisNo')} name="chassisNo" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col md={24}>
                          <Form.Item label={t('busesPage_MotorPower')} name="motorPower" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                    <Card size="small" className="mtopo-card" title={t('busesPage_StaffList')}>
                      <Form.Item name="staffIds">
                        <Checkbox.Group className="checkbox-option-single-line w-100">
                          {busDrivers.map(driver => {
                            return (
                              <div>
                                <Checkbox value={driver.id} style={{ width: '100%' }} className="label-100">
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    {getFullName(driver.firstName, driver.lastName)}
                                    <b>{driver.roles?.filter(x => [TransportRoleType.CoDriver, TransportRoleType.Driver].includes(x)).join(', ')}</b>
                                  </div>
                                </Checkbox>
                              </div>
                            );
                          })}
                        </Checkbox.Group>
                      </Form.Item>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card size="small" className="mtopo-card" title={t('busesPage_SeatingDetail')}>
                      <Form.Item label={t('busesPage_BusMode')} name="busMode">
                        <Select>
                          <Select.Option key="Available">{t('Available')}</Select.Option>
                        </Select>
                      </Form.Item>
                      <Form.Item label={t('busesPage_SeatingModel')} name="seatingModelId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <SeatingModelInput />
                      </Form.Item>
                      <Form.Item label={t('busesPage_AcquisitionDate')} name="acquisitionDate">
                        <AppDatePicker className="w-100" />
                      </Form.Item>
                      <Form.Item label={t('busesPage_BaggageCapacity')} name="baggageCapacity">
                        <Input />
                      </Form.Item>
                      <Form.Item label={t('busesPage_InitialColor')} name="initialColor">
                        <ColorPicker />
                      </Form.Item>
                    </Card>
                    <SeatingSummary seatingModelId={seatingModelId} />
                  </Col>
                  <Col md={12}>
                    <SeatingModel seatingModelId={seatingModelId} ticketClasses={ticketClasses} />
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {busDocumentId ? (
        <BusDocumentsModal
          busId={busDocumentId}
          onClose={() => {
            setBusDocumentId(undefined);
          }}
        />
      ) : null}
      {showExportModal ? (
        <ExportColumnsSelectorModal
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      {busInsuranceId ? (
        <BusInsuranceModal
          busId={busInsuranceId}
          onClose={() => {
            setBusInsuranceId(undefined);
          }}
        />
      ) : null}
      {bus2StationId ? (
        <BusStationMappingsModal
          busId={bus2StationId}
          onClose={() => {
            setBus2StationId(undefined);
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default BusesPage;
